import React, { useState, useEffect, useRef, useCallback } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SmartTable from "./SmartTable";
import { signIn, getIdToken, getUserId, signOut } from "./auth";
import { sendMessageStream, ping } from "./api";
import type { Components } from "react-markdown";
import "./App.css";

// Normalize image srcs so merch assets in the app's /public folder resolve correctly.
// Handles "merch/x.svg", "/merch/x.svg", "./merch/x.svg", "public/merch/x.svg".
// Leaves absolute/external (http, //, data:) URLs untouched.
function normalizeImgSrc(src?: string): string {
  if (!src) return "";
  const s = src.trim();
  if (/^(https?:)?\/\//i.test(s) || s.startsWith("data:")) return s;
  const cleaned = s.replace(/^\.?\/+/, "").replace(/^public\//, "");
  return "/" + cleaned;
}

const markdownComponents: Components = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  table: ({ children }) => <SmartTable>{children}</SmartTable>,
  img: ({ src, alt }) => {
    const resolved = normalizeImgSrc(typeof src === "string" ? src : "");
    const isMerch = resolved.startsWith("/merch/");
    return (
      <img
        src={resolved}
        alt={alt || "product image"}
        className={isMerch ? "merch-img" : "md-img"}
        loading="lazy"
      />
    );
  },
};

interface MerchProduct {
  img: string;
  name: string;
  desc: string;
  productId: string;
  price: string;
  sizes: string[];
}

// Parse the agent's merch-list markdown into structured products so we can render
// interactive cards with a Buy button. Returns null if no product blocks match
// (callers then fall back to rendering the raw markdown).
function parseMerch(
  text: string
): { products: MerchProduct[]; before: string; after: string } | null {
  const re =
    /!\[[^\]]*\]\(([^)\s]+)\)\s*\n+\s*\*\*(.+?)\*\*\s*[—-]?\s*([^\n]*)\n+\s*Product ID:\s*(\d+).*?Price:\s*([\d.]+)\s*ETH.*?Sizes:\s*([^\n]+)/gi;
  const products: MerchProduct[] = [];
  let firstStart = -1;
  let lastEnd = -1;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (firstStart < 0) firstStart = m.index;
    lastEnd = m.index + m[0].length;
    products.push({
      img: m[1],
      name: m[2].trim(),
      desc: m[3].trim(),
      productId: m[4],
      price: m[5],
      sizes: m[6]
        .split(/[,·]/)
        .map((s) => s.trim())
        .filter(Boolean),
    });
  }
  if (products.length === 0) return null;
  return {
    products,
    before: text.slice(0, firstStart).trim(),
    after: text.slice(lastEnd).trim(),
  };
}

function MerchCard({
  product,
  disabled,
  onBuy,
}: {
  product: MerchProduct;
  disabled: boolean;
  onBuy: (name: string, size: string) => void;
}) {
  const [size, setSize] = useState(
    product.sizes.includes("M") ? "M" : product.sizes[0] || "M"
  );
  return (
    <div className="merch-card">
      <img
        src={normalizeImgSrc(product.img)}
        alt={product.name}
        className="merch-card-img"
        loading="lazy"
      />
      <div className="merch-name">{product.name}</div>
      {product.desc && <div className="merch-desc">{product.desc}</div>}
      <div className="merch-price">{product.price} ETH</div>
      <div className="merch-actions">
        <select
          aria-label={`Size for ${product.name}`}
          value={size}
          onChange={(e) => setSize(e.target.value)}
          disabled={disabled || product.sizes.length === 0}
        >
          {product.sizes.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="buy-btn"
          disabled={disabled}
          onClick={() => onBuy(product.name, size)}
        >
          Buy
        </button>
      </div>
    </div>
  );
}

interface Message {
  role: "user" | "assistant";
  text: string;
}

interface Chat {
  id: string;
  title: string;
  messages: Message[];
  sessionId?: string;
}

type View = "login" | "chat";

function ThinkingDots() {
  return (
    <div className="message assistant">
      <div className="thinking">
        <span className="dot" />
        <span className="dot" />
        <span className="dot" />
      </div>
    </div>
  );
}

function sessionKey(userId: string): string {
  return `agentSessions:${userId}`;
}

function loadSessions(userId: string): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(sessionKey(userId)) || "{}");
  } catch {
    return {};
  }
}

function saveSession(userId: string, chatId: string, runtimeSessionId: string): void {
  const sessions = loadSessions(userId);
  sessions[chatId] = runtimeSessionId;
  localStorage.setItem(sessionKey(userId), JSON.stringify(sessions));
}

function createChat(): Chat {
  return { id: crypto.randomUUID(), title: "New Chat", messages: [] };
}

export default function App() {
  const [view, setView] = useState<View>("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [idToken, setIdToken] = useState<string | null>(null);
  const [chats, setChats] = useState<Chat[]>([createChat()]);
  const [activeChatId, setActiveChatId] = useState(chats[0].id);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEnd = useRef<HTMLDivElement>(null);
  const pinged = useRef(false);
  const activeChatIdRef = useRef(activeChatId);

  activeChatIdRef.current = activeChatId;
  const activeChat = chats.find((c) => c.id === activeChatId)!;

  useEffect(() => {
    const token = getIdToken();
    if (token) {
      setIdToken(token);
      setView("chat");
      const userId = getUserId();
      if (userId) {
        const sessions = loadSessions(userId);
        setChats((prev) =>
          prev.map((c) => (sessions[c.id] ? { ...c, sessionId: sessions[c.id] } : c))
        );
      }
    }
  }, []);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeChat.messages, streaming]);

  useEffect(() => {
    if (view === "chat" && idToken && !pinged.current) {
      pinged.current = true;
      // Warm-up ping (fans out to BitcoinKB + Merchant runtimes via the invoke Lambda).
      // Throttle: only send if the last ping was more than 1 hour ago.
      const PING_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
      const last = Number(localStorage.getItem("lastWarmupPingAt") || "0");
      if (Date.now() - last > PING_INTERVAL_MS) {
        ping(idToken);
        localStorage.setItem("lastWarmupPingAt", String(Date.now()));
      }
    }
  }, [view, idToken]);

  const updateChat = useCallback((chatId: string, updater: (chat: Chat) => Chat) => {
    setChats((prev) => prev.map((c) => (c.id === chatId ? updater(c) : c)));
  }, []);

  const handleNewChat = () => {
    const newChat = createChat();
    setChats((prev) => [newChat, ...prev]);
    setActiveChatId(newChat.id);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const token = await signIn(username.trim(), password);
      setPassword("");
      setIdToken(token);
      setView("chat");
    } catch (err: any) {
      setError(err.message || "Sign-in failed");
    }
  };

  const sendMessage = async (rawText: string) => {
    const userMsg = rawText.trim();
    if (!userMsg || !idToken || streaming) return;

    const chatId = activeChatId;
    setError("");

    // Add user message and empty assistant message
    updateChat(chatId, (c) => ({
      ...c,
      title: c.messages.length === 0 ? userMsg.slice(0, 30) : c.title,
      messages: [
        ...c.messages,
        { role: "user", text: userMsg },
        { role: "assistant", text: "" },
      ],
    }));
    setStreaming(true);

    await sendMessageStream(userMsg, idToken, {
      onChunk: (text) => {
        updateChat(chatId, (c) => {
          const msgs = [...c.messages];
          const last = msgs[msgs.length - 1];
          msgs[msgs.length - 1] = { ...last, text: last.text + text };
          return { ...c, messages: msgs };
        });
      },
      onSessionId: (sessionId) => {
        updateChat(chatId, (c) => ({ ...c, sessionId }));
        const userId = getUserId();
        if (userId) saveSession(userId, chatId, sessionId);
      },
      onDone: () => setStreaming(false),
      onError: (err) => {
        setStreaming(false);
        setError(err);
        // Remove empty assistant message on error
        updateChat(chatId, (c) => {
          const msgs = [...c.messages];
          if (msgs.length > 0 && msgs[msgs.length - 1].role === "assistant" && !msgs[msgs.length - 1].text) {
            msgs.pop();
          }
          return { ...c, messages: msgs };
        });
      },
    }, activeChat.sessionId);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || streaming) return;
    const t = input.trim();
    setInput("");
    await sendMessage(t);
  };

  // Buy button on a t-shirt card: drive the purchase flow with an explicit,
  // self-contained prompt (includes the product name so the agent needn't rely
  // on session memory of a prior selection).
  const handleBuy = (name: string, size: string) => {
    void sendMessage(`user wants to buy "${name}" size ${size}`);
  };

  const renderAssistant = (text: string) => {
    const parsed = parseMerch(text);
    if (!parsed) {
      return (
        <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {text || "..."}
        </Markdown>
      );
    }
    return (
      <>
        {parsed.before && (
          <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {parsed.before}
          </Markdown>
        )}
        <div className="merch-cards">
          {parsed.products.map((p, idx) => (
            <MerchCard key={`${p.productId}-${idx}`} product={p} disabled={streaming} onBuy={handleBuy} />
          ))}
        </div>
        {parsed.after && (
          <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {parsed.after}
          </Markdown>
        )}
      </>
    );
  };

  const handleSignOut = () => {
    const userId = getUserId();
    if (userId) localStorage.removeItem(sessionKey(userId));
    signOut();
    setIdToken(null);
    const fresh = createChat();
    setChats([fresh]);
    setActiveChatId(fresh.id);
    setView("login");
  };

  if (view === "login") {
    return (
      <div className="container">
        <div className="auth-card">
          <h1>🔗 Blockchain Chatbot</h1>
          <form onSubmit={handleLogin}>
            <input type="text" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required />
            <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
            <button type="submit">Sign In</button>
          </form>
          {error && <p className="error">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      {sidebarOpen && (
        <div className="sidebar">
          <button className="new-chat-btn" onClick={handleNewChat}>+ New Chat</button>
          <div className="chat-list">
            {chats.map((c) => (
              <div
                key={c.id}
                className={`chat-item ${c.id === activeChatId ? "active" : ""}`}
                onClick={() => setActiveChatId(c.id)}
              >
                {c.title}
              </div>
            ))}
          </div>
          <button onClick={handleSignOut} className="sign-out">Sign Out</button>
        </div>
      )}
      <div className="chat-container">
        <div className="chat-header">
          <button className="toggle-sidebar" onClick={() => setSidebarOpen((v) => !v)}>☰</button>
          <h2>🔗 Blockchain Chatbot</h2>
        </div>
        <div className="messages">
          {activeChat.messages.map((msg, i) => (
            <div key={i} className={`message ${msg.role}`}>
              {msg.role === "assistant" ? (
                renderAssistant(msg.text)
              ) : (
                <span>{msg.text}</span>
              )}
            </div>
          ))}
          {streaming && activeChat.messages[activeChat.messages.length - 1]?.text === "" && <ThinkingDots />}
          <div ref={messagesEnd} />
        </div>
        <form className="input-bar" onSubmit={handleSend}>
          <textarea
            placeholder="Ask about blockchain... (Shift+Enter for new line)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={streaming}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            onInput={(e) => {
              const t = e.currentTarget;
              t.style.height = "auto";
              t.style.height = Math.min(t.scrollHeight, 150) + "px";
            }}
          />
          <button type="submit" disabled={streaming || !input.trim()}>Send</button>
        </form>
        {error && <p className="error">{error}</p>}
      </div>
    </div>
  );
}
